'use client';

import { useState } from 'react';
import Link from 'next/link';

const QUESTIONS = [
  { id: 1, text: 'Jeg føler meg emosjonelt utmattet av jobben min.' },
  { id: 2, text: 'Jeg føler meg oppbrukt ved slutten av arbeidsdagen.' },
  { id: 3, text: 'Jeg føler meg sliten når jeg står opp om morgenen og må møte en ny arbeidsdag.' },
  { id: 4, text: 'Jeg har blitt mer kynisk eller likegyldig til arbeidet mitt.' },
  { id: 5, text: 'Jeg tviler på betydningen av arbeidet mitt.' },
  { id: 6, text: 'Jeg har vanskeligheter med å konsentrere meg.' },
  { id: 7, text: 'Jeg føler at jeg ikke presterer like godt som før.' },
  { id: 8, text: 'Jeg er mer irritabel eller utolmodig enn vanlig.' },
  { id: 9, text: 'Jeg har fysiske symptomer som hodepine, muskelspenninger eller søvnproblemer.' },
  { id: 10, text: 'Jeg trekker meg tilbake fra sosiale situasjoner eller kollegaer.' },
];

const OPTIONS = [
  { value: 0, label: 'Aldri' },
  { value: 1, label: 'Sjelden' },
  { value: 2, label: 'Av og til' },
  { value: 3, label: 'Ofte' },
  { value: 4, label: 'Alltid' },
];

function getResult(score: number): { level: string; color: string; bg: string; border: string; description: string; advice: string[] } {
  if (score <= 10) return {
    level: 'Lav risiko',
    color: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    description: 'Du viser få tegn på utbrenthet. Det betyr ikke at du bør ignorere stress — men akkurat nå ser det ut til at du håndterer belastningen godt.',
    advice: [
      'Fortsett med det som fungerer — bevisst hvile, grenser og prioritering',
      'Vær oppmerksom på endringer over tid',
      'En coach kan hjelpe deg bygge bærekraftige vaner før stresset øker',
    ],
  };
  if (score <= 20) return {
    level: 'Moderat risiko',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Du viser noen tegn på begynnende utbrenthet. Dette er et vanlig stadie — og det er nå du har muligheten til å snu utviklingen.',
    advice: [
      'Identifiser de største energityvene i hverdagen',
      'Sett tydeligere grenser mellom jobb og fritid',
      'Vurder å snakke med en coach om stressmestring og prioritering',
      'Prioriter søvn og restitusjon de neste ukene',
    ],
  };
  if (score <= 30) return {
    level: 'Høy risiko',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    description: 'Du viser tydelige tegn på utbrenthet. Dette er ikke uvanlig, men det er viktig å ta det på alvor nå — før det eskalerer.',
    advice: [
      'Snakk med noen du stoler på om situasjonen',
      'Vurder å redusere arbeidsbelastning midlertidig',
      'En coach kan hjelpe deg lage en konkret plan for å komme tilbake i balanse',
      'Hvis symptomene påvirker helsen din, kontakt fastlegen',
    ],
  };
  return {
    level: 'Svært høy risiko',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    description: 'Du scorer svært høyt på tegn på utbrenthet. Det er viktig å ta dette på alvor og søke hjelp.',
    advice: [
      'Kontakt fastlegen din for en helsevurdering',
      'Vurder sykemelding eller tilrettelegging',
      'En coach eller terapeut kan hjelpe deg lage en plan for recovery',
      'Du er ikke alene — over 1 av 4 nordmenn opplever dette i løpet av karrieren',
    ],
  };
}

export function UtbrenthetTest() {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResult, setShowResult] = useState(false);

  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === QUESTIONS.length;
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const maxScore = QUESTIONS.length * 4;
  const result = getResult(score);

  return (
    <main className="min-h-screen gradient-warm">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-xs text-stone-400 mb-8 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link>
          <span>&rsaquo;</span>
          <Link href="/ressurser" className="hover:text-stone-600">Ressurser</Link>
          <span>&rsaquo;</span>
          <span className="text-stone-600">Utbrenthet-test</span>
        </nav>

        <div className="mb-8">
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700">Mental helse</span>
          <h1 className="text-3xl font-semibold text-stone-900 leading-tight mt-3 mb-3">Er du p&aring; vei mot utbrenthet?</h1>
          <p className="text-stone-500 text-base leading-relaxed mb-6">10 sp&oslash;rsm&aring;l basert p&aring; Maslach Burnout Inventory (MBI). Det tar 2 minutter. Resultatet gir deg en indikasjon &mdash; ikke en diagnose.</p>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/tools/utbrenthet-test.jpg" alt="Utbrenthet-test (MBI)" className="w-full h-full object-cover" loading="eager"/>
          </div>
        </div>

        {!showResult ? (
          <>
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs text-stone-400 mb-1.5">
                <span>{answeredCount} av {QUESTIONS.length} besvart</span>
                <span>{Math.round((answeredCount / QUESTIONS.length) * 100)}%</span>
              </div>
              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div className="h-full bg-stone-900 rounded-full transition-all duration-300" style={{ width: `${(answeredCount / QUESTIONS.length) * 100}%` }} />
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {QUESTIONS.map((q) => (
                <div key={q.id} className="bg-white border border-stone-200 rounded-xl p-5">
                  <div className="text-sm font-medium text-stone-900 mb-3">{q.id}. {q.text}</div>
                  <div className="flex flex-wrap gap-2">
                    {OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          answers[q.id] === opt.value
                            ? 'bg-stone-900 text-white border-stone-900'
                            : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Submit */}
            <button
              onClick={() => allAnswered && setShowResult(true)}
              disabled={!allAnswered}
              className="w-full mt-6 py-3.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400 transition-colors"
            >
              {allAnswered ? 'Se resultatet →' : `Besvar alle spørsmål (${QUESTIONS.length - answeredCount} gjenstår)`}
            </button>
          </>
        ) : (
          <>
            {/* Result */}
            <div className={`rounded-2xl p-6 border ${result.border} ${result.bg} mb-6`}>
              <div className="text-center mb-4">
                <div className={`text-2xl font-bold ${result.color}`}>{result.level}</div>
                <div className="text-sm text-stone-500 mt-1">Score: {score} av {maxScore}</div>
              </div>

              {/* Score bar */}
              <div className="h-3 bg-white rounded-full overflow-hidden mb-4">
                <div className={`h-full rounded-full transition-all duration-500 ${
                  score <= 10 ? 'bg-green-500' : score <= 20 ? 'bg-amber-500' : score <= 30 ? 'bg-orange-500' : 'bg-red-500'
                }`} style={{ width: `${(score / maxScore) * 100}%` }} />
              </div>

              <div className="flex justify-between text-[10px] text-stone-400 mb-4">
                <span>Lav risiko</span><span>Moderat</span><span>H&oslash;y</span><span>Sv&aelig;rt h&oslash;y</span>
              </div>

              <p className="text-sm text-stone-600 leading-relaxed">{result.description}</p>
            </div>

            {/* Advice */}
            <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
              <h2 className="font-semibold text-stone-900 text-sm mb-3">Hva kan du gj&oslash;re?</h2>
              <ul className="space-y-2">
                {result.advice.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <span className="text-stone-400 mt-0.5">&#10003;</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-stone-900 text-white rounded-2xl p-6 text-center mb-6">
              <h2 className="font-semibold text-lg mb-2">Vil du snakke med noen?</h2>
              <p className="text-stone-400 text-sm mb-4">En coach hjelper deg lage en konkret plan for &aring; komme tilbake i balanse.</p>
              <Link href="/#waitlist-client" className="inline-block px-6 py-2.5 bg-white text-stone-900 rounded-xl text-sm font-medium hover:bg-stone-100 transition-colors">
                Finn din coach &rarr;
              </Link>
            </div>

            {/* Restart */}
            <button onClick={() => { setAnswers({}); setShowResult(false); }}
              className="w-full py-2.5 border border-stone-200 rounded-xl text-sm text-stone-500 hover:bg-stone-50 transition-colors">
              Ta testen p&aring; nytt
            </button>

            {/* Disclaimer */}
            <p className="text-[11px] text-stone-400 text-center mt-4 leading-relaxed">
              Denne testen er basert p&aring; Maslach Burnout Inventory (MBI) og gir en indikasjon, ikke en diagnose.
              Ved alvorlige symptomer, kontakt fastlegen din. Kilde: Maslach, C. &amp; Jackson, S.E. (1981).
            </p>
          </>
        )}

        {/* Relatert innhold */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center text-sm">
          <Link href="/ressurser/artikler/utbrenthet-symptomer" className="text-stone-400 hover:text-stone-700 hover:underline">Utbrenthet-symptomer &rarr;</Link>
          <span className="text-stone-200">&middot;</span>
          <Link href="/ressurser/artikler/stressmestring-teknikker" className="text-stone-400 hover:text-stone-700 hover:underline">Stressmestring &rarr;</Link>
          <span className="text-stone-200">&middot;</span>
          <Link href="/ressurser" className="text-stone-400 hover:text-stone-700 hover:underline">Alle ressurser &rarr;</Link>
        </div>
      </div>
    </main>
  );
}
